import React from "react";
import { Link } from "react-router-dom";
import { Mail, Instagram, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { SITE, buildWaUrl } from "../data/site";
import { AREAS_BATAM, AREAS_KEPRI } from "../data/areas";
import { BoltIcon, ArcDivider } from "./Decorations";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative mt-24 bg-[#0F4C75] text-white overflow-hidden"
    >
      <div
        className="absolute inset-0 grid-bg opacity-20"
        aria-hidden="true"
      />
      <div
        className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-40"
        style={{
          background:
            "radial-gradient(closest-side, rgba(255,107,0,0.6), transparent 70%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-20 pb-10">
        {/* Huge CTA */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 pb-14 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3 text-[#FF6B00] text-sm tracking-[0.3em] font-semibold uppercase mb-4">
              <BoltIcon className="w-4 h-5" />
              Butuh listrik? Kami siap
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight">
              Butuh jasa listrik <br className="hidden sm:inline" />
              <span className="text-[#FF6B00]">di Batam?</span>
            </h2>
            <p className="mt-4 text-white/70 max-w-xl text-base md:text-lg">
              Respon dalam 15 menit. Teknisi bersertifikat. Bergaransi.
            </p>
          </div>
          <a
            href={buildWaUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-wa-cta"
            className="group inline-flex items-center gap-3 bg-[#FF6B00] hover:bg-[#ff7a1f] text-white font-bold rounded-full pl-7 pr-5 py-4 text-base shadow-[0_12px_30px_rgba(255,107,0,0.4)] transition-all hover:-translate-y-1"
          >
            Chat WhatsApp sekarang
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-[#FF6B00]">
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </a>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 py-14">
          {/* Brand */}
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-3">
              <span className="relative inline-flex items-center justify-center w-12 h-12 rounded-xl bg-black overflow-hidden">
                <img
                  src={SITE.logo}
                  alt="ListrikBatam logo"
                  className="w-full h-full object-cover"
                />
              </span>
              <span className="font-display font-extrabold text-xl">
                Listrik<span className="text-[#FF6B00]">Batam</span>.com
              </span>
            </Link>
            <p className="mt-5 text-white/70 text-sm leading-relaxed max-w-md">
              Layanan teknis kelistrikan dan peralatan rumah tangga dalam satu
              solusi terpadu. Melayani Batam & Kepulauan Riau dengan standar
              PUIL 2011 & SNI.
            </p>

            <div className="mt-5 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wider">
              {["Listrik", "Service AC", "CCTV", "Water Heater", "Kulkas", "Mesin Cuci", "Panel Industri"].map((t) => (
                <span key={t} className="inline-flex items-center bg-white/10 border border-white/15 px-2 py-1 rounded-full text-white/70 font-semibold">
                  {t}
                </span>
              ))}
            </div>

            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex gap-3 items-start">
                <Phone className="w-4 h-4 mt-0.5 text-[#FF6B00]" />
                <a
                  href={`tel:+${SITE.whatsappNumber}`}
                  className="hover:text-[#FF6B00] transition-colors"
                  data-testid="footer-phone"
                >
                  +62 811-7006-5656
                </a>
              </li>
              <li className="flex gap-3 items-start">
                <Mail className="w-4 h-4 mt-0.5 text-[#FF6B00]" />
                <a
                  href={`mailto:${SITE.email}`}
                  className="hover:text-[#FF6B00] transition-colors break-all"
                  data-testid="footer-email"
                >
                  {SITE.email}
                </a>
              </li>
              <li className="flex gap-3 items-start">
                <Instagram className="w-4 h-4 mt-0.5 text-[#FF6B00]" />
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#FF6B00] transition-colors"
                  data-testid="footer-instagram"
                >
                  {SITE.instagramHandle}
                </a>
              </li>
              <li className="flex gap-3 items-start">
                <MapPin className="w-4 h-4 mt-0.5 text-[#FF6B00]" />
                <span>{SITE.address}</span>
              </li>
            </ul>
          </div>

          {/* Quick links */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF6B00] mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3 text-sm text-white/80">
              <li><Link to="/" className="hover:text-[#FF6B00]">Home</Link></li>
              <li><Link to="/layanan" className="hover:text-[#FF6B00]">Layanan</Link></li>
              <li><Link to="/layanan#service-ac" className="hover:text-[#FF6B00]">Service AC</Link></li>
              <li><Link to="/layanan#instalasi-cctv" className="hover:text-[#FF6B00]">Instalasi CCTV</Link></li>
              <li><Link to="/tentang" className="hover:text-[#FF6B00]">Tentang</Link></li>
              <li><Link to="/blog" className="hover:text-[#FF6B00]">Blog</Link></li>
              <li><Link to="/kontak" className="hover:text-[#FF6B00]">Kontak</Link></li>
            </ul>
          </div>

          {/* Areas - Batam */}
          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF6B00] mb-5">
              Area Batam
            </h4>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-white/75">
              {AREAS_BATAM.slice(0, 12).map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/jasa-listrik/${a.slug}`}
                    className="hover:text-[#FF6B00]"
                  >
                    {a.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Areas - Kepri */}
          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF6B00] mb-5">
              Kepulauan Riau
            </h4>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-white/75">
              {AREAS_KEPRI.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/jasa-listrik/${a.slug}`}
                    className="hover:text-[#FF6B00]"
                  >
                    {a.name}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <iframe
                title="Google Maps ListrikBatam"
                src={SITE.mapsEmbed}
                className="w-full h-36 rounded-xl border border-white/15 grayscale contrast-[1.1]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        <ArcDivider className="w-full h-3 opacity-60" />

        <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
          <p>
            © {new Date().getFullYear()} ListrikBatam.com — Jasa Listrik
            Profesional Batam & Kepulauan Riau. All rights reserved.
          </p>
          <p className="text-white/50">
            Dibangun dengan teliti, dikerjakan dengan hati.
          </p>
        </div>
      </div>
    </footer>
  );
}
